import { useCallback, useEffect, useMemo } from "react";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormDivider from "@components/FormDivider/FormDivider";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { paths } from "@utils/navigation";
import type { TFunction } from "i18next";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";
import { useCompleteTask } from "../hooks/useCompleteTask";
import { DRAWER_TEXT, LANG_BASE, TASK_STATUS, TEST_ID_BASE } from "../VesselStatus";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  vesselId: string;
  type: "approval" | "reject";
};

const FORM_FIELDS = {
  INTERNAL_NOTES: "internalNotes",
  INTERNAL_NOTES_DOCUMENT: "internalNotesDocument",
  INTERNAL_NOTES_DOCUMENT_NAME: "internalNotesDocumentName",
  EXTERNAL_NOTES: "externalNotes",
  EXTERNAL_NOTES_DOCUMENT: "externalNotesDocument",
  EXTERNAL_NOTES_DOCUMENT_NAME: "externalNotesDocumentName",
} as const;

const getApproveRejectSchema = (t: TFunction) =>
  z.object({
    [FORM_FIELDS.INTERNAL_NOTES]: z
      .string()
      .max(
        1000,
        t("form-validation.max-length", {
          label: t(`${LANG_BASE}.delegation-reason`),
          max: 1000,
        }),
      )
      .optional(),
    [FORM_FIELDS.INTERNAL_NOTES_DOCUMENT]: z.string().optional(),
    [FORM_FIELDS.INTERNAL_NOTES_DOCUMENT_NAME]: z.string().optional(),
    [FORM_FIELDS.EXTERNAL_NOTES]: z.string().max(
      1000,
      t("form-validation.max-length", {
        label: t(`${LANG_BASE}.delegation-reason`),
        max: 1000,
      }),
    ),
    [FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT]: z.string().optional(),
    [FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT_NAME]: z.string().optional(),
  });

const ApproveRejectSideDrawer = ({ isOpen, onClose, vesselId, type }: Props) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const { clearFilesForKey, setFilesForKey, filesByKey } = useUploadStore();

  const internalNoteFile = filesByKey[FORM_FIELDS.INTERNAL_NOTES_DOCUMENT] || [];
  const externalNoteFile = filesByKey[FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT] || [];

  const uploadConditions = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedSideDrawerExtensions = uploadConditions.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const c = useMemo(() => DRAWER_TEXT()[type], [type]);

  const form = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(getApproveRejectSchema(t)),
    shouldUnregister: true,
    defaultValues: {
      [FORM_FIELDS.INTERNAL_NOTES]: "",
      [FORM_FIELDS.INTERNAL_NOTES_DOCUMENT]: "",
      [FORM_FIELDS.INTERNAL_NOTES_DOCUMENT_NAME]: "",
      [FORM_FIELDS.EXTERNAL_NOTES]: "",
      [FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT]: "",
      [FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT_NAME]: "",
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
    clearFilesForKey(FORM_FIELDS.INTERNAL_NOTES_DOCUMENT);
    clearFilesForKey(FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT);
  };

  const handleSuccess = () => {
    handleClose();
    navigate(`/${paths.vesselManagement.index}`);
  };

  const handleError = () => {
    handleClose();
  };

  useEffect(() => {
    clearFilesForKey(FORM_FIELDS.INTERNAL_NOTES_DOCUMENT);
    clearFilesForKey(FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT);
  }, []);

  const { mutate: completeTask, isPending } = useCompleteTask({ handleSuccess, handleError });

  const onValid = async ({
    internalNotes,
    internalNotesDocument,
    internalNotesDocumentName,
    externalNotes,
    externalNotesDocument,
    externalNotesDocumentName,
  }: {
    internalNotes?: string;
    internalNotesDocument?: string;
    internalNotesDocumentName?: string;
    externalNotes: string;
    externalNotesDocument?: string;
    externalNotesDocumentName?: string;
  }) => {
    if (internalNotesUpload.isPending || externalNotesUpload.isPending) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }
    completeTask({
      service: "bff-vessel",
      vesselId,
      params: {
        status: type === "approval" ? TASK_STATUS.APPROVED : TASK_STATUS.REJECTED,
        internalNotes,
        internalNotesDocument,
        internalNotesDocumentName,
        externalNotes,
        externalNotesDocument,
        externalNotesDocumentName,
      },
      successTitle: t(`vessel-management.notifications.${c.successMessage}`),
      errorTitle: t(`vessel-management.notifications.${c.errorMessage}`),
    });
  };

  const onSubmit = () => {
    form.handleSubmit(onValid)();
  };

  const mutationFn = ({ file, documentType }: { file: File; documentType: string }) =>
    runTransaction(
      {
        module: "vessel_registration",
        screen: "ApproveRejectVessel",
        action: "upload_vessel_task_document",
        entityType: "vessel_document",
      },
      async (transactionId) => {
        const payloadForSasToken = {
          prefix: SasTokenPrefix.VESSEL_REGISTRATION,
          contentType: file.type || null,
          privilege: SasTokenPrivilege.WRITE,
          blobUrl: null,
        };

        const { blobUrl, sasToken } = await getSasToken(
          payloadForSasToken,
          ...(transactionId ? [transactionId] : []),
        );

        const [uploadResult] = await uploadFileToAzureBlob({
          file,
          blobUrl,
          sasToken,
          certificateType: documentType,
        });

        const documentUrl = uploadResult?.documentUrl ?? blobUrl;
        return { documentUrl, documentType };
      },
    );

  const internalNotesUpload = useMutation({
    mutationFn,
  });

  const externalNotesUpload = useMutation({
    mutationFn,
  });

  const handleInternalNotesUpload = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (!file) return;

      internalNotesUpload.mutate(
        { file, documentType: "INTERNAL_NOTES" },
        {
          onSuccess: ({ documentUrl }) => {
            form.setValue(FORM_FIELDS.INTERNAL_NOTES_DOCUMENT, documentUrl);
            form.setValue(FORM_FIELDS.INTERNAL_NOTES_DOCUMENT_NAME, file.name);
            setFilesForKey(FORM_FIELDS.INTERNAL_NOTES_DOCUMENT, [
              {
                name: file.name,
                url: documentUrl,
              },
            ]);
          },
          onError: () => {
            clearFilesForKey(FORM_FIELDS.INTERNAL_NOTES_DOCUMENT);
            notify({
              title: t("vessel-management.notifications.attachments-upload-error"),
              description: t(
                "vessel-management.notifications.attachments-upload-error-description",
              ),
              type: "error",
            });
          },
        },
      );
    },
    [clearFilesForKey, internalNotesUpload, form, notify, setFilesForKey, t],
  );

  const handleExternalNotesUpload = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (!file) return;

      externalNotesUpload.mutate(
        { file, documentType: "EXTERNAL_NOTES" },
        {
          onSuccess: ({ documentUrl }) => {
            form.setValue(FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT, documentUrl);
            form.setValue(FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT_NAME, file.name);
            setFilesForKey(FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT, [
              {
                name: file.name,
                url: documentUrl,
              },
            ]);
          },
          onError: () => {
            clearFilesForKey(FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT);
            notify({
              title: t("vessel-management.notifications.attachments-upload-error"),
              description: t(
                "vessel-management.notifications.attachments-upload-error-description",
              ),
              type: "error",
            });
          },
        },
      );
    },
    [clearFilesForKey, externalNotesUpload, form, notify, setFilesForKey, t],
  );

  const handleRemoveInternalNotesFile = useCallback(() => {
    form.setValue(FORM_FIELDS.INTERNAL_NOTES_DOCUMENT, "");
    form.setValue(FORM_FIELDS.INTERNAL_NOTES_DOCUMENT_NAME, "");
    clearFilesForKey(FORM_FIELDS.INTERNAL_NOTES_DOCUMENT);
  }, [clearFilesForKey, form]);

  const handleRemoveExternalNotesFile = useCallback(() => {
    form.setValue(FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT, "");
    form.setValue(FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT_NAME, "");
    clearFilesForKey(FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT);
  }, [clearFilesForKey, form]);

  return (
    <FormProvider {...form}>
      <SideDrawer
        isOpen={isOpen}
        onClose={handleClose}
        onSubmit={onSubmit}
        keepMounted={false}
        title={t(`${LANG_BASE}.${c.title}`)}
        description={t(`${LANG_BASE}.${c.description}`)}
        submitLabel={t(`${LANG_BASE}.${c.submit}`)}
        isSubmitting={isPending}
      >
        <div className="w-[400px] p-6 flex flex-col gap-6">
          <FormItem
            name={FORM_FIELDS.EXTERNAL_NOTES}
            type="textarea"
            label={t(`${LANG_BASE}.externalNotes`)}
            testId={`${TEST_ID_BASE}-external-notes`}
            minRows={4}
            maxChars={1000}
            showCharCounter
          />
          {externalNoteFile[0] ? (
            <div className="MuiFormControl-root MuiFormControl-fullWidth css-kjxwpk-MuiFormControl-root">
              <CustomFormLabel sx={{ mb: 1 }}>
                {t(`${LANG_BASE}.externalAttachments`)}
              </CustomFormLabel>
              <SingleFile
                testId={`${TEST_ID_BASE}-attachment-0`}
                file={externalNoteFile[0]}
                fieldName={externalNoteFile[0].name}
                currentFileIndex={0}
                setFiles={handleRemoveExternalNotesFile}
                isLoading={externalNotesUpload.isPending}
              />
            </div>
          ) : (
            <DocumentUpload
              name={FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT}
              label={t(`${LANG_BASE}.externalAttachments`)}
              testId={`${TEST_ID_BASE}-approve-reject-attachments`}
              allowedExtensions={allowedSideDrawerExtensions}
              conditions={uploadConditions}
              maxFiles={1}
              onUpload={handleExternalNotesUpload}
              setFiles={(next) =>
                setFilesForKey(FORM_FIELDS.EXTERNAL_NOTES_DOCUMENT, next as UploadEntry[])
              }
              files={externalNoteFile}
            />
          )}
          <FormDivider />
          <FormItem
            name={FORM_FIELDS.INTERNAL_NOTES}
            type="textarea"
            label={t(`${LANG_BASE}.internalNotes`)}
            testId={`${TEST_ID_BASE}-internal-notes`}
            minRows={4}
            maxChars={1000}
            showCharCounter
          />
          {internalNoteFile[0] ? (
            <div className="MuiFormControl-root MuiFormControl-fullWidth css-kjxwpk-MuiFormControl-root">
              <CustomFormLabel sx={{ mb: 1 }}>
                {t(`${LANG_BASE}.internalAttachments`)}
              </CustomFormLabel>
              <SingleFile
                testId={`${TEST_ID_BASE}-attachment-0`}
                file={internalNoteFile[0]}
                fieldName={internalNoteFile[0].name}
                currentFileIndex={0}
                setFiles={handleRemoveInternalNotesFile}
                isLoading={internalNotesUpload.isPending}
              />
            </div>
          ) : (
            <DocumentUpload
              name={FORM_FIELDS.INTERNAL_NOTES_DOCUMENT}
              label={t(`${LANG_BASE}.internalAttachments`)}
              testId={`${TEST_ID_BASE}-approve-reject-attachments`}
              allowedExtensions={allowedSideDrawerExtensions}
              conditions={uploadConditions}
              maxFiles={1}
              onUpload={handleInternalNotesUpload}
              setFiles={(next) =>
                setFilesForKey(FORM_FIELDS.INTERNAL_NOTES_DOCUMENT, next as UploadEntry[])
              }
              files={internalNoteFile}
            />
          )}
        </div>
      </SideDrawer>
    </FormProvider>
  );
};

export default ApproveRejectSideDrawer;
